import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { PageLayout } from "../components/PageLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import { 
  MapPin, 
  Plus, 
  Pencil, 
  Trash2,
  CalendarIcon
} from "lucide-react";

export default function LocaisPage() {
  const { user } = useAuth();
  const [locais, setLocais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocal, setEditingLocal] = useState(null);
  const [formData, setFormData] = useState({
    nome: "",
    capacidade_total: "",
    licenca_inicio: new Date(),
    licenca_fim: new Date(new Date().setFullYear(new Date().getFullYear() + 1))
  });

  useEffect(() => {
    fetchLocais();
  }, []);

  const fetchLocais = async () => {
    try {
      const response = await axios.get(`${API}/locais`);
      setLocais(response.data);
    } catch (error) {
      toast.error("Erro ao carregar locais");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        nome: formData.nome,
        capacidade_total: parseInt(formData.capacidade_total),
        licenca_inicio: format(formData.licenca_inicio, "yyyy-MM-dd"),
        licenca_fim: format(formData.licenca_fim, "yyyy-MM-dd")
      };

      if (editingLocal) {
        await axios.put(`${API}/locais/${editingLocal.id}`, payload);
        toast.success("Local atualizado");
      } else {
        await axios.post(`${API}/locais`, payload);
        toast.success("Local criado");
      }
      
      setDialogOpen(false);
      setEditingLocal(null);
      resetForm();
      fetchLocais();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao guardar local");
    }
  };

  const handleEdit = (local) => {
    setEditingLocal(local);
    setFormData({
      nome: local.nome,
      capacidade_total: local.capacidade_total.toString(),
      licenca_inicio: new Date(local.licenca_inicio),
      licenca_fim: new Date(local.licenca_fim)
    });
    setDialogOpen(true);
  };

  const handleDelete = async (localId) => {
    if (!confirm("Tem a certeza que deseja eliminar este local?")) return;
    try {
      await axios.delete(`${API}/locais/${localId}`);
      toast.success("Local eliminado");
      fetchLocais();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao eliminar local");
    }
  };

  const resetForm = () => {
    setFormData({
      nome: "",
      capacidade_total: "",
      licenca_inicio: new Date(),
      licenca_fim: new Date(new Date().setFullYear(new Date().getFullYear() + 1))
    });
  };

  const isLicenseExpired = (licencaFim) => {
    return new Date(licencaFim) < new Date();
  };

  const headerAction = (
    <Dialog open={dialogOpen} onOpenChange={(open) => {
      setDialogOpen(open);
      if (!open) {
        setEditingLocal(null);
        resetForm();
      }
    }}>
      <DialogTrigger asChild>
        <Button className="bg-[#0047FF] hover:bg-[#0035CC]" data-testid="add-local-btn">
          <Plus className="w-4 h-4 mr-2" />
          Novo Local
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editingLocal ? "Editar Local" : "Novo Local"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nome">Nome do Local</Label>
            <Input
              id="nome"
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              placeholder="Ex: Parque Central"
              required
              data-testid="local-nome-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="capacidade">Capacidade Total</Label>
            <Input
              id="capacidade"
              type="number"
              min="1"
              value={formData.capacidade_total}
              onChange={(e) => setFormData({ ...formData, capacidade_total: e.target.value })}
              placeholder="Ex: 200"
              required
              data-testid="local-capacidade-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Início da Licença</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="local-licenca-inicio">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(formData.licenca_inicio, "dd/MM/yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.licenca_inicio}
                    onSelect={(date) => date && setFormData({ ...formData, licenca_inicio: date })}
                    locale={pt}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Fim da Licença</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="local-licenca-fim">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(formData.licenca_fim, "dd/MM/yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.licenca_fim}
                    onSelect={(date) => date && setFormData({ ...formData, licenca_fim: date })}
                    locale={pt}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 bg-[#0047FF] hover:bg-[#0035CC]" data-testid="local-submit-btn">
              {editingLocal ? "Guardar" : "Criar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <PageLayout title="Locais" subtitle="Gestão de parques de estacionamento" headerAction={headerAction}>
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0047FF]"></div>
        </div>
      ) : locais.length === 0 ? (
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-8 text-center">
            <MapPin className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
            <p className="text-[#64748B] mb-4">Nenhum local registado</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-[#0047FF] hover:bg-[#0035CC]">
              <Plus className="w-4 h-4 mr-2" />
              Criar Primeiro Local
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-[#E2E8F0]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead className="text-center">Capacidade</TableHead>
                <TableHead className="text-center">Ocupação</TableHead>
                <TableHead className="text-center">Licença</TableHead>
                <TableHead className="text-center">Estado</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {locais.map((local) => (
                <TableRow key={local.id} data-testid={`local-row-${local.id}`}>
                  <TableCell className="font-medium">{local.nome}</TableCell>
                  <TableCell className="text-center">{local.capacidade_total}</TableCell>
                  <TableCell className="text-center">
                    <span className="font-medium">{local.ocupacao_atual || 0}</span>
                    <span className="text-[#64748B]"> / {local.capacidade_total}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="text-sm">
                      <div>{format(new Date(local.licenca_inicio), "dd/MM/yyyy")}</div>
                      <div className="text-[#64748B]">até {format(new Date(local.licenca_fim), "dd/MM/yyyy")}</div>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={isLicenseExpired(local.licenca_fim) ? "destructive" : "default"}
                      className={isLicenseExpired(local.licenca_fim) ? "bg-[#D50000]" : "bg-[#00C853]"}>
                      {isLicenseExpired(local.licenca_fim) ? "Expirada" : "Ativa"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(local)} data-testid={`edit-local-${local.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-[#D50000]" onClick={() => handleDelete(local.id)} data-testid={`delete-local-${local.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </PageLayout>
  );
}
